/**
 * Hartenjagen at the table.
 *
 * Sibling of `LivePlay`: same shell, same table, same fanned hand, different
 * rules on top. What is genuinely new here is the passing screen — three cards
 * chosen at once, by everyone, in secret.
 */

import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type { Card } from '@shared/types/game';
import {
  cardsEqual,
  heartsLegalPlays,
  heartsRulesFor,
  rankLabel,
  sortHeartsHand,
} from '@shared/game-logic';
import { Button, Empty, Panel, Screen } from '@/components/ui';
import { PlayingCard } from '@/components/PlayingCard';
import FannedHand from '@/components/FannedHand';
import Avatar from '@/components/Avatar';
import HeartsTable from '@/components/HeartsTable';
import Notices from '@/components/Notices';
import { useHeartsSession, type HeartsActions } from '@/live/useHeartsSession';
import { playCue } from '@/lib/feedback';
import { SUIT_LABELS, seatColor } from '@/lib/labels';
import { isMyHeartsTurn, myHeartsHand, myHeartsPlayer, useHearts } from '@/store/hearts';

const DIRECTION_LABEL: Record<string, string> = {
  left: 'naar links',
  right: 'naar rechts',
  across: 'naar de overkant',
  none: 'niemand — je houdt alles',
};

const cardName = (card: Card) => `${rankLabel(card.rank)} ${SUIT_LABELS[card.suit]}`;

export default function HeartsPlay() {
  const { code } = useParams<{ code: string }>();
  const location = useLocation() as { state?: { name?: string; profileId?: string | null } };
  const actions = useHeartsSession(
    code,
    location.state?.name ?? null,
    location.state?.profileId ?? null
  );

  const data = useHearts();
  const { state, status, error, notices } = data;

  if (!state) {
    return (
      <Screen title={`Hartenjagen ${code ?? ''}`} back="/">
        <Panel title="Verbinden">
          <p className="text-sm text-slate-400">
            {status === 'reconnecting'
              ? 'De verbinding is weg. Er wordt opnieuw geprobeerd…'
              : 'Bezig met verbinden met het potje…'}
          </p>
          {error && <p className="mt-2 text-sm text-bad">{error}</p>}
        </Panel>
      </Screen>
    );
  }

  return (
    <Screen
      title={state.phase === 'lobby' ? `Code ${state.code}` : `Ronde ${state.handNo}`}
      back="/"
      subtitle={
        state.phase === 'lobby'
          ? `${state.players.length} van 4 spelers`
          : // Which of the two games, in the one line that is always on screen:
            // the totals mean different things in the two, so the name has to be
            // somewhere you cannot miss it.
            `${state.variant === 'nederlands' ? 'Nederlands' : 'Amerikaans'} · tot ${state.targetScore} punten · laagste wint`
      }
    >
      {error && (
        <p
          role="alert"
          className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200"
        >
          {error}
        </p>
      )}

      {state.phase === 'lobby' && <Lobby actions={actions} />}

      {state.phase === 'passing' && (
        <>
          <HeartsTable state={state} mySeat={data.seat} />
          <PassPanel actions={actions} />
        </>
      )}

      {state.phase === 'playing' && (
        <>
          <HeartsTable state={state} mySeat={data.seat} />
          <HandPanel actions={actions} />
        </>
      )}

      {state.phase === 'scoring' && (
        <>
          {/* The table stays up: this is the only chance anyone gets to see who
              took the queen, and clearing it would answer nothing. */}
          <HeartsTable state={state} mySeat={data.seat} />
          <HandResult />
        </>
      )}

      {state.phase === 'finished' && <Finished />}

      <Standings />

      <Notices items={notices} show={state.phase !== 'lobby'} />
    </Screen>
  );
}

function Lobby({ actions }: { actions: HeartsActions }) {
  const data = useHearts();
  const state = data.state!;
  const me = myHeartsPlayer(data);
  const joinUrl = `${window.location.origin}/harten/${state.code}`;
  const full = state.players.length === 4;
  const everyoneReady = state.players.every((player) => player.isReady);
  const bots = state.players.filter((player) => player.isBot).length;

  return (
    <>
      <Panel title="Deel deze code">
        <p className="text-center font-tabular text-5xl font-bold tracking-[0.2em]">{state.code}</p>
        <p className="mt-3 break-all text-center text-sm text-slate-400">{joinUrl}</p>
        <p className="mt-3 text-center text-sm text-slate-500">
          Hartenjagen speel je met precies vier.
        </p>
      </Panel>

      <Panel title="Aan tafel">
        <ul className="space-y-2">
          {state.players.map((player) => (
            <li
              key={player.seat}
              className="flex items-center gap-3 rounded-xl bg-base-700/60 px-3 py-2"
            >
              <Avatar player={player} size={32} seatColor={seatColor(player.seat)} />
              <span className="flex-1 font-medium">
                {player.name}
                {player.isBot && <span className="ml-2 text-xs text-slate-500">computer</span>}
              </span>
              <span className={player.isReady ? 'text-sm text-good' : 'text-sm text-slate-500'}>
                {player.isReady ? 'klaar' : 'wacht'}
              </span>
            </li>
          ))}
        </ul>
      </Panel>

      <div className="space-y-2">
        {me?.isHost && (
          <div className="flex gap-2">
            <Button full onClick={actions.addBot} disabled={full}>
              Computerspeler erbij
            </Button>
            {bots > 0 && (
              <Button full variant="secondary" onClick={actions.removeBot}>
                Laatste weg
              </Button>
            )}
          </div>
        )}
        {me && !me.isHost && (
          <Button
            full
            variant={me.isReady ? 'secondary' : 'primary'}
            onClick={() => actions.ready(!me.isReady)}
          >
            {me.isReady ? 'Toch nog even niet' : 'Ik ben klaar'}
          </Button>
        )}
        {me?.isHost && (
          <Button full variant="primary" disabled={!full || !everyoneReady} onClick={actions.start}>
            {full ? 'Beginnen' : `Wachten op ${4 - state.players.length} speler(s)`}
          </Button>
        )}
      </div>
    </>
  );
}

function PassPanel({ actions }: { actions: HeartsActions }) {
  const data = useHearts();
  const state = data.state!;
  const me = myHeartsPlayer(data);
  const selection = data.selection;
  const toggle = useHearts((s) => s.toggleSelection);

  const hand = sortHeartsHand(myHeartsHand(data));
  const chosen = (card: Card) => selection.some((c) => cardsEqual(c, card));
  const waiting = state.players.filter((player) => !player.hasPassed).length;

  if (me?.hasPassed) {
    return (
      <Panel title="Doorgegeven">
        <p className="text-sm text-slate-400">
          Je drie kaarten staan klaar. Nog {waiting} {waiting === 1 ? 'speler' : 'spelers'} te gaan
          — niemand ziet iets tot iedereen gekozen heeft.
        </p>
      </Panel>
    );
  }

  return (
    <Panel title={`Kies drie kaarten ${DIRECTION_LABEL[state.passDirection]}`}>
      <p className="mb-2 text-sm text-slate-400">
        Iedereen kiest tegelijk en niemand ziet wat er aankomt. Meestal doe je de schoppenvrouw weg,
        of maak je een kleur leeg.
      </p>

      <FannedHand cardWidth={56} className="py-2">
        {hand.map((card, index) => (
          <PlayingCard
            key={`${card.suit}-${card.rank}`}
            card={card}
            size="md"
            dimmed={selection.length >= 3 && !chosen(card)}
            onClick={() => toggle(card)}
            label={`${chosen(card) ? 'Kies niet' : 'Kies'} ${cardName(card)}`}
            /*
             * The lift is a margin, not a transform. `animate-deal-in` ends on
             * a transform with `fill-mode: both`, and an animation beats a
             * plain declaration — so `-translate-y-3` here would work for
             * exactly as long as the deal animation lasts and then silently
             * stop. Third time this has come up; see the README.
             */
            className={`animate-deal-in ${chosen(card) ? 'mb-4 ring-2 ring-accent-400' : ''}`}
            style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
          />
        ))}
      </FannedHand>

      <div className="mt-3">
        <Button
          full
          variant="primary"
          disabled={selection.length !== 3}
          onClick={() => actions.pass(selection)}
        >
          {selection.length === 3 ? 'Doorgeven' : `Nog ${3 - selection.length} kiezen`}
        </Button>
      </div>
    </Panel>
  );
}

function HandPanel({ actions }: { actions: HeartsActions }) {
  const data = useHearts();
  const state = data.state!;
  const hand = sortHeartsHand(myHeartsHand(data));
  const playable = isMyHeartsTurn(data);
  const [refused, setRefused] = useState<string | null>(null);

  // A new trick clears the explanation; it no longer applies.
  const trickLength = state.currentTrick.length;
  useEffect(() => setRefused(null), [trickLength, state.handNo]);

  /*
   * The same rule function the server uses, so what is greyed out here and what
   * would be refused there cannot drift apart. The server still decides — this
   * is only so a child does not tap a card and get told off for no visible
   * reason.
   */
  const legal = playable
    ? heartsLegalPlays(hand, {
        trick: state.currentTrick,
        heartsBroken: state.heartsBroken,
        isFirstTrick: state.isFirstTrick,
        // From the table's own state: the two variants differ on the opening card
        // and on whether the first trick is safe.
        rules: heartsRulesFor(state.variant),
      })
    : [];
  const isLegal = (card: Card) => legal.some((c) => cardsEqual(c, card));

  const waitingOn =
    !playable && state.turnSeat !== null ? state.players[state.turnSeat]?.name : null;

  return (
    <Panel title={playable ? 'Jij bent aan zet' : 'Jouw kaarten'}>
      {!playable && waitingOn && (
        <p className="mb-3 text-sm text-slate-400">Wachten op {waitingOn}…</p>
      )}

      <FannedHand cardWidth={56} className="py-2">
        {hand.map((card, index) => {
          const allowed = isLegal(card);
          return (
            <PlayingCard
              key={`${card.suit}-${card.rank}`}
              card={card}
              size="md"
              dimmed={playable && !allowed}
              // Still tappable: a dead button reads as broken to a child, while
              // a tap that explains itself teaches the rule.
              disabled={!playable}
              onClick={() => {
                if (allowed) {
                  setRefused(null);
                  actions.play(card);
                } else {
                  setRefused(whyNot(card, hand, state));
                  playCue({ kind: 'refused' });
                }
              }}
              label={`Speel ${cardName(card)}${playable && !allowed ? ' (mag nu niet)' : ''}`}
              className="animate-deal-in"
              style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
            />
          );
        })}
      </FannedHand>

      {refused && (
        <p
          role="alert"
          className="mt-3 animate-pop-in rounded-xl border-l-2 border-amber-400 bg-amber-400/10 px-3 py-2 text-sm text-amber-100"
        >
          {refused}
        </p>
      )}
    </Panel>
  );
}

/** The same sentences the server would send, so the reason is never a surprise. */
function whyNot(
  card: Card,
  hand: Card[],
  state: { currentTrick: unknown[]; heartsBroken: boolean; isFirstTrick: boolean }
): string {
  const trick = state.currentTrick as Array<{ card: Card }>;
  if (trick.length === 0) {
    if (state.isFirstTrick) return 'De klaveren 2 komt uit.';
    if (card.suit === 'hearts' && !state.heartsBroken) return 'Harten zijn nog niet gebroken.';
  } else {
    const led = trick[0].card.suit;
    if (hand.some((c) => c.suit === led) && card.suit !== led) return 'Je moet kleur bekennen.';
    if (state.isFirstTrick) return 'In de eerste slag mag je geen punten weggooien.';
  }
  return 'Die kaart mag je nu niet spelen.';
}

function HandResult() {
  const state = useHearts((s) => s.state)!;
  const last = state.hands[state.hands.length - 1];
  if (!last) return null;

  return (
    <Panel title={`Ronde ${last.handNo} geteld`}>
      {last.moonSeat !== null && (
        <p className="mb-3 animate-pop-in rounded-xl border border-accent-500/40 bg-accent-500/10 px-3 py-2 text-center font-medium text-accent-200">
          🌕 {state.players[last.moonSeat]?.name} schiet de maan!
        </p>
      )}
      <ul className="space-y-1">
        {state.players.map((player) => (
          <li key={player.seat} className="flex items-center gap-3 text-sm">
            <Avatar player={player} size={28} seatColor={seatColor(player.seat)} />
            <span className="flex-1">{player.name}</span>
            <span className="tabular-nums text-slate-400">+{last.points[player.seat]}</span>
            <span className="w-10 text-right font-semibold tabular-nums">
              {last.totals[player.seat]}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-slate-500">De volgende ronde begint zo vanzelf.</p>
    </Panel>
  );
}

function Standings() {
  const state = useHearts((s) => s.state);
  if (!state || state.phase === 'lobby') return null;

  // Ascending: lowest wins, which is the thing everyone gets wrong once.
  const order = [...state.players].sort((a, b) => a.total - b.total);

  return (
    <Panel title="Stand — laagste wint">
      <ul className="space-y-1">
        {order.map((player, index) => (
          <li key={player.seat} className="flex items-center gap-3 text-sm">
            <span className="w-4 text-slate-500">{index + 1}.</span>
            <Avatar player={player} size={28} seatColor={seatColor(player.seat)} />
            <span className="flex-1 truncate">{player.name}</span>
            {player.handPoints > 0 && (
              <span className="text-xs text-bad">+{player.handPoints} deze ronde</span>
            )}
            <span className="w-10 text-right font-semibold tabular-nums">{player.total}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Finished() {
  const state = useHearts((s) => s.state)!;
  const order = [...state.players].sort((a, b) => a.total - b.total);
  const winner = order[0];

  return (
    <Panel title="Afgelopen">
      {winner ? (
        <p className="text-center text-lg font-semibold">
          {winner.name} wint met {winner.total} punten.
        </p>
      ) : (
        <Empty>Geen uitslag.</Empty>
      )}
      <ul className="mt-3 space-y-1">
        {order.map((player, index) => (
          <li key={player.seat} className="flex items-center gap-3 text-sm">
            <span className="w-4 text-slate-500">{index + 1}.</span>
            <Avatar player={player} size={40} seatColor={seatColor(player.seat)} />
            <span className="flex-1">{player.name}</span>
            <span className="font-semibold tabular-nums">{player.total}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
